<script setup>
// The time-and-place picker (BOOKINGS_PLAN.md B7b, B7c, B7d, BE1, BE7, BE9, BE14), shown ONLY when the club takes
// bookings for the match's sport (`bookingsActive`); every form keeps its old inputs otherwise. The parent owns the
// data (useSchedulingAvailability) and turns the choice into the request (pickerRequest).
//
// X214 (Grant 2026-10-28: "Why not use the date picker we already use for scheduling, then offer court/playing area
// options. I always prefer the table view for court bookings ... It's not even clear that I am booking a court at
// all."): three steps, primary action first.
//   1 When: the date picker and 24h time selects every scheduling form uses (SimpleDateTimeSelect); an admin's
//     one-off match length under it.
//   2 Book a court (the club's own word: table, board, playing area): "Book a court | No court needed", then the
//     member /book table for the chosen day (the shared BookingDayGrid and BookingCell: playing areas as columns, the
//     day's starts as rows, the legend in the corner), the picked start drawn over the rows the match books, prev /
//     next day beside the heading. A cell can be picked only when the match fits from there.
//   3 The summary ("Thu 30 Oct · 19:00–20:00 · Court 2 · £8.00"), the hold tick, who pays, and one line on what
//     happens to the court. The dialog's button says the rest (utils/scheduleBooking pickerPrimaryLabel).
// Two columns when the picker is wide (a desktop dialog: When and the summary on the left, sticky; the table on the
// right), one above the other when narrow (a phone: the table scrolls sideways with its time column pinned).
import {aOrAn, areaWords, cap} from '~/utils/areaWords';
import {computed, ref, watch} from 'vue';
import SimpleDateTimeSelect from '~/components/fields/SimpleDateTimeSelect.vue';
import MatchPayerPicker from '~/components/bookings/MatchPayerPicker.vue';
import BookingDayGrid from '~/components/bookings/BookingDayGrid.vue';
import BookingLegend from '~/components/bookings/BookingLegend.vue';
import ChoiceRadioList from '~/components/admin/bookings/ChoiceRadioList.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import TlDialog from '~/components/TlDialog.vue';
import {formatMinor} from '~/utils/adminActionCopy';
import {
  alreadyBookedFor, chosenCell, chosenGroupId, dateOfInstant, dateWord, dayOpenPlay, dayPassed, farAheadLine, findDay,
  holdHelp, holdLabel, isPayerWarning, lengthWord, matchLengthChoices, openPlayChoices, payerOptions, payerRefusal, showHold,
  showPayer, splitLine, timeChipDisabled, viewerShareConfirmLine,
} from '~/utils/schedulingPicker';
import {
  bookingMinutesOf, bookingNoun, bookStepTitle, cellIdle, courtLine, dateLoaded, dayLoadState, firstFreeStart, gridDefaultPlace, hhmmInZone, noFreeStartLine, offSlotLine, scheduleGrid,
  offTableKeep, offTableLine, PAST_LADDER_HINT, PAST_LADDER_PLACE, pastClosingConfirm, typedRowKeep, pastClosingLine, scheduleLegendKeys, scheduleSummary, selectedSpan, shiftDate, startHasPassed, startsAtFor, timeAtClock, whatHappensLine,
} from '~/utils/scheduleBooking';
import {listWords} from '~/utils/sessionMove';
import {gridTime} from '~/utils/bookings';
import {dateInZone} from '~/utils/bookingsMember';

const props = defineProps({
  // SchedulingAvailabilityResponse (bookingsActive true)
  availability: {type: Object, required: true},
  // propose | confirm | accept | set
  mode: {type: String, default: 'propose'},
  // the side that still has to agree ("Hold Table 5 until Ravi replies")
  otherName: {type: String, default: null},
  // the area the form came in with (the proposal's, the fixture's)
  preferredAreaId: {type: String, default: null},
  // a club admin setting the time: the one-off match length
  admin: {type: Boolean, default: false},
  elsewhere: {type: Boolean, default: true},
  loading: {type: Boolean, default: false},
  // X214: the form's own line about the time ("Alex will need to confirm this time"), shown in the summary
  note: {type: String, default: null},
  // X244 (TEST_R102 M-A): the match's court and time as they stand, and whether a club admin is moving the whole
  // session ({areaId, scheduledAt, session}); a time the table doesn't offer keeps that court where the API keeps it.
  kept: {type: Object, default: null},
  // F-FIX7 (TEST_FT-R5 observation 1): a ladder match, whose save refuses a time that has passed: the line says its
  // booking stays, never that it is released.
  refusesPast: {type: Boolean, default: false},
});
// {date, time, startsAt, place, areaId, hold, payerUserId, durationMinutes, touched}
const choice = defineModel({type: Object, default: () => ({})});
const emit = defineEmits(['reload']);

const zone = computed(() => props.availability?.timeZone ?? null);
const today = computed(() => dateInZone(zone.value));
// Grant 2026-11-02: the club's own words ("court", "court or board" / "courts and boards"), "playing area" only when none is known.
const words = computed(() => areaWords(props.availability));
const noun = computed(() => words.value.noun);
const nounCap = computed(() => cap(noun.value));
const pluralCap = computed(() => cap(words.value.plural));
const areaOrder = computed(() => [...(props.availability?.areas ?? [])]
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || String(a.name).localeCompare(String(b.name))).map((a) => a.id));

const update = (patch) => {
  choice.value = {...(choice.value ?? {}), ...patch};
};

// ── Step 1: when ──
const selectedDate = computed(() => choice.value?.date ?? null);
const day = computed(() => findDay(props.availability, selectedDate.value));
const openPlay = computed(() => dayOpenPlay(day.value));
const dayGone = computed(() => !!selectedDate.value && selectedDate.value < today.value);
// X214 fix: the days a window has been asked for (a reschedule opens on the match's own day, which can sit past the
// first 14), so a missing day is fetched once and then says it failed with Retry, never an endless skeleton.
const asked = ref(new Set());
const ask = (date) => {
  asked.value = new Set([...asked.value, date]);
  emit('reload', {from: date});
};
const dayState = computed(() => dayLoadState(props.availability, selectedDate.value, today.value,
    {asked: asked.value.has(selectedDate.value), loading: props.loading}));
const retryDay = () => { if (selectedDate.value) ask(selectedDate.value); };
const time = computed(() => timeAtClock(day.value, choice.value?.time, zone.value));

// "Book a court" or "No court needed": what the viewer wants, kept while a time with no start in the table is picked.
const wantCourt = ref(null);
// X219 (R89 M-2): why a new schedule opened on No court needed (nothing free for the whole match from its time on).
const noFreeReason = ref('');

// The place for a start: the area already chosen if the match still fits there, else the first free one (the
// viewer asked for a court), else none.
const placeFor = (t) => {
  const c = choice.value ?? {};
  if (!wantCourt.value) return {place: c.place === 'elsewhere' ? 'elsewhere' : 'later', areaId: null, allowPastClosing: false};
  if (c.place === 'area' && c.areaId) {
    const still = (t?.areas ?? []).find((a) => a.areaId === c.areaId);
    if (still && (still.status === 'free' || still.status === 'mine')) return {place: 'area', areaId: c.areaId, allowPastClosing: false};
    // X237: an admin's Book anyway stays on its start while the table reloads (the same start, still only past closing).
    if (still && still.status === 'closed' && still.pastClosing && c.allowPastClosing && !!t?.startsAt && new Date(t.startsAt).getTime() === new Date(c.startsAt).getTime()) return {place: 'area', areaId: c.areaId, allowPastClosing: true};
  }
  return {...gridDefaultPlace('set', t, props.preferredAreaId, areaOrder.value), allowPastClosing: false};
};

const applyWhen = (date, hhmm) => {
  const c = choice.value ?? {};
  const d = findDay(props.availability, date);
  const t = timeAtClock(d, hhmm, zone.value);
  const startsAt = t?.startsAt ?? startsAtFor(date, hhmm, zone.value);
  const next = {date, time: hhmm, startsAt};
  if (dayOpenPlay(d)) {
    // nothing to book: an area is only noted on the match
    const noted = c.place === 'area' && (props.availability?.areas ?? []).some((a) => a.id === c.areaId);
    Object.assign(next, noted ? {place: 'area', areaId: c.areaId} : {place: c.place === 'elsewhere' ? 'elsewhere' : 'later', areaId: null}, {allowPastClosing: false, offTable: null});
  } else if (t) {
    // X247 (TEST_R104 M-1): a typed start that IS a row, where the match's court is Too short or closed and no court fits:
    // the keep-the-court rule of a time off the table (an admin's single match or whole session), so the save asks Book
    // anyway as a tap on that cell or a typed 20:40 does, never "No court booked" with the booking released.
    const place = placeFor(t);
    const keep = place.place !== 'area' && wantCourt.value ? typedRowKeep(props.availability, props.kept, t, startsAt) : null;
    Object.assign(next, keep
        ? {place: 'area', areaId: props.kept.areaId, offTable: keep, allowPastClosing: false}
        : {...place, offTable: null});
  } else {
    // X244 (TEST_R102 M-A): a time the table doesn't offer keeps the match's booked court where the API keeps it (its own
    // time under way; an admin's whole session), never "No court booked" over bookings that stay or move.
    const keep = wantCourt.value ? offTableKeep(props.availability, props.kept, startsAt) : null;
    Object.assign(next, keep
        ? {place: 'area', areaId: props.kept.areaId, offTable: keep}
        : {place: !wantCourt.value && c.place === 'elsewhere' ? 'elsewhere' : 'later', areaId: null, offTable: null}, {allowPastClosing: false});
  }
  const same = next.startsAt === c.startsAt && next.place === c.place && next.areaId === c.areaId;
  next.hold = same ? !!c.hold : false;
  update(next);
};

const setWhen = (date, hhmm) => {
  if (!date || !hhmm) {
    update({date: date ?? null, time: hhmm ?? null, startsAt: date && hhmm ? startsAtFor(date, hhmm, zone.value) : null, allowPastClosing: false});
    return;
  }
  if (!dateLoaded(props.availability, date) && date >= today.value) {
    // the table for that day is not loaded yet: keep the time, fetch from that day, and pick the place when it lands
    update({date, time: hhmm, startsAt: startsAtFor(date, hhmm, zone.value), place: choice.value?.place === 'elsewhere' ? 'elsewhere' : 'later', areaId: null, hold: false, allowPastClosing: false});
    ask(date);
    return;
  }
  applyWhen(date, hhmm);
};
const whenDate = computed({get: () => choice.value?.date ?? null, set: (v) => setWhen(v, choice.value?.time ?? null)});
const whenTime = computed({get: () => choice.value?.time ?? null, set: (v) => setWhen(choice.value?.date ?? null, v)});
// X244: the session choice ("Move just this match") changes what a time off the table keeps; read it again.
watch(() => JSON.stringify(props.kept ?? null), () => {
  const c = choice.value ?? {};
  // X247: a typed start in the table that kept the court (offTable) is read again too.
  if (c.date && c.time && wantCourt.value && (!time.value || c.offTable) && dateLoaded(props.availability, c.date)) applyWhen(c.date, c.time);
});
const lengthText = computed(() => {
  const m = choice.value?.durationMinutes || props.availability?.matchMinutes;
  return m ? `Matches last about ${lengthWord(m)}.` : null;
});

// ── Step 2: book a court ──
const grid = computed(() => scheduleGrid(props.availability, selectedDate.value));
const legendItems = computed(() => scheduleLegendKeys(grid.value, noun.value));
const selected = computed(() => (choice.value?.place === 'area' ? selectedSpan(grid.value, choice.value.areaId, choice.value.startsAt) : null));
const explain = ref('');
const offSlot = computed(() => !!wantCourt.value && !openPlay.value && !dayGone.value && !!day.value && (day.value.times?.length ?? 0) > 0 && !time.value && !!choice.value?.time);
const nothingLeft = computed(() => !!day.value && !openPlay.value && !(day.value.times ?? []).some((t) => !timeChipDisabled(t)));
const dayLabel = computed(() => (selectedDate.value ? dateWord(selectedDate.value) : ''));
const canEarlier = computed(() => !!selectedDate.value && selectedDate.value > today.value);

const pickCourt = (on) => {
  explain.value = '';
  noFreeReason.value = '';
  wantCourt.value = on;
  if (!on) {
    update({place: choice.value?.place === 'elsewhere' ? 'elsewhere' : 'later', areaId: null, hold: false, touched: true, allowPastClosing: false, offTable: null});
    return;
  }
  const t = time.value;
  update({...(t ? placeFor(t) : {place: 'later', areaId: null}), hold: false, touched: true, offTable: null});
};
const noCourtItems = computed(() => [
  {value: 'later', title: `We'll sort the ${noun.value} ourselves`, sub: `Nothing is booked now. ${cap(aOrAn(noun.value))} can be booked later.`},
  ...(props.elsewhere ? [{value: 'elsewhere', title: "We're playing somewhere else", sub: 'Nothing is booked here.'}] : []),
]);
const noCourtChoice = computed({
  get: () => (choice.value?.place === 'elsewhere' ? 'elsewhere' : 'later'),
  set: (v) => update({place: v, areaId: null, hold: false, touched: true, allowPastClosing: false}),
});

const pick = (cell, column, allowPastClosing = false) => {
  explain.value = '';
  wantCourt.value = true;
  // X247: a tap is a pick from the table, never a kept court (offTable), whatever was typed before.
  update({startsAt: cell.startsAt, time: hhmmInZone(cell.startsAt, zone.value), place: 'area', areaId: column.area.id, hold: false, touched: true, allowPastClosing, offTable: null});
};
// X237 (Grant 2026-10-29, "Admins can, with a warning"): a Too short start that only runs past closing is an admin's
// to pick (the API marks it); it asks first, and Book anyway sends allowPastClosing with the save.
const closingAsk = ref(null); // {cell, column, words}
const closingOpen = computed({get: () => !!closingAsk.value, set: (v) => { if (!v) closingAsk.value = null; }});
const onTap = ({cell, column}) => {
  // X244 (TEST_R102 L-4): the first shown row of the match's own block that started before the table (a session under
  // way today) keeps the match's own start, as X243's first-row rule says, never the first start still to come.
  if (cell?.keepStartsAt) {
    explain.value = '';
    wantCourt.value = true;
    setWhen(dateOfInstant(cell.keepStartsAt, zone.value), hhmmInZone(cell.keepStartsAt, zone.value));
    return;
  }
  if (cell?.pastClosing) {
    explain.value = '';
    closingAsk.value = {cell, column, words: pastClosingConfirm(column.area.name, cell.closesAtLabel, bookingMinutesOf(props.availability, choice.value), bookingNoun(props.availability), noun.value)};
    return;
  }
  pick(cell, column);
};
const bookAnyway = () => {
  const ask = closingAsk.value;
  closingAsk.value = null;
  if (ask) pick(ask.cell, ask.column, true);
};
const onExplain = ({cell, reason, tip}) => {
  explain.value = reason || tip || cell?.tip || '';
};
const stepDay = (n) => {
  if (!selectedDate.value) return;
  explain.value = '';
  const next = shiftDate(selectedDate.value, n);
  if (next < today.value) return;
  setWhen(next, choice.value?.time ?? null);
};

// An open-play day: nothing to book, an area can only be noted on the match.
const openPlaces = computed(() => openPlayChoices(props.availability?.areas, {elsewhere: false}));
const openPlaceKey = computed(() => (choice.value?.place === 'area' ? choice.value.areaId : 'later'));
const pickOpenPlace = (p) => update({place: p.place, areaId: p.place === 'area' ? p.areaId : null, hold: false, touched: true});

// ── Step 3: the summary ──
const placeName = computed(() => {
  if (choice.value?.place !== 'area') return null;
  return (props.availability?.areas ?? []).find((a) => a.id === choice.value.areaId)?.name ?? null;
});
const cell = computed(() => chosenCell(time.value, choice.value));
const groupId = computed(() => chosenGroupId(props.availability, time.value, choice.value));
const idle = computed(() => cellIdle(cell.value, choice.value));

// Hold (B7b, BE1)
const holdShown = computed(() => showHold(props.availability, props.mode, time.value, choice.value));
const hold = computed({
  get: () => !!choice.value?.hold,
  set: (v) => update({hold: !!v}),
});

// Who pays (BE9): the confirmer by default (the API's default payer), any of the match's players with an account.
const payerShown = computed(() => showPayer(props.availability, props.mode, choice.value, groupId.value, time.value));
const payers = computed(() => (payerShown.value ? payerOptions(props.availability, groupId.value) : []));
const payer = computed({
  get: () => choice.value?.payerUserId ?? props.availability?.defaultPayerUserId ?? null,
  set: (v) => update({payerUserId: v}),
});
const payerRefused = computed(() => payerRefusal(payers.value.find((p) => p.userId === payer.value), props.availability, groupId.value));
const payerHint = computed(() => {
  const p = payers.value.find((x) => x.userId === payer.value);
  if (!p || p.priceMinor <= 0) return null;
  return p.hasSavedCard
      ? `${p.isViewer ? 'Your' : p.name + "'s"} saved card is charged when the time is confirmed.`
      : `${p.isViewer ? 'You' : p.name} ${p.isViewer ? 'have' : 'has'} no saved card: the booking waits for payment and is released if it isn't paid in time.`;
});
// R10 L1: an open-play (or closed) area books nothing, so there is no split to speak of.
const split = computed(() => (['area', 'any'].includes(choice.value?.place) && props.mode !== 'propose' && !cellIdle(cell.value, choice.value)) ? splitLine(props.availability, groupId.value) : null);
// R11 L5 (web parity): confirming books it and charges the confirmer's half now, so the split line says so, as the
// challenge page's Confirm time dialog does.
const splitMine = computed(() => (split.value && ['confirm', 'accept'].includes(props.mode) && choice.value?.place === 'area') ? viewerShareConfirmLine(props.availability, groupId.value) : null);
// R12 L4: the two sentences are joined with a space ("…for you). Your £3…").
const splitText = computed(() => (split.value ? [`${split.value}.`, splitMine.value].filter(Boolean).join(' ') : null));
const payerPrice = computed(() => (payerShown.value ? payers.value.find((p) => p.userId === payer.value)?.priceMinor : undefined));
const priceLine = computed(() => {
  const minor = cell.value?.priceMinor;
  if (!(minor > 0) || props.mode === 'propose' || payerShown.value || split.value) return null;
  return `${formatMinor(minor, props.availability?.currency || 'gbp', {exact: true})} for the booking, charged when the time is confirmed.`;
});

// X237: the summary says the admin chose to book past closing (the booking carries the Admin chip).
const pastClosingText = computed(() => (choice.value?.place === 'area' && choice.value?.allowPastClosing && cell.value?.pastClosing
    ? pastClosingLine(cell.value.closesAtLabel) : null));
// X243 (TEST_FT-R3 L-B): a member's unchanged time on the court already booked for the match (an admin's set time keeps
// "booked for both sides now").
const alreadyBooked = computed(() => props.mode !== 'set' && alreadyBookedFor(props.availability, choice.value, cell.value));
const summary = computed(() => scheduleSummary(props.availability, choice.value, {areaName: placeName.value, cell: cell.value, priceMinor: payerPrice.value, noun: noun.value}));
// X244 (TEST_R102 M-A): a time the table doesn't offer, on a match with a booking (its own or its session's), says what
// happens to that booking: kept, moved with the whole session, or released (a time that has passed books nothing). A
// whole-session move with no court chosen keeps every court too (the API moves the session's bookings either way).
const sessionKeepsCourts = computed(() => !!props.kept?.session && !['area', 'elsewhere'].includes(choice.value?.place) && (props.availability?.ownBookings ?? []).length > 0);
const offTableText = computed(() => {
  const c = choice.value ?? {};
  if (openPlay.value || c.place === 'elsewhere' || !c.startsAt) return null;
  const keep = c.place === 'area' ? (c.offTable ?? null) : sessionKeepsCourts.value && !startHasPassed(c.startsAt) ? 'session' : null;
  if (!keep && (c.place === 'area' || c.place === 'any')) return null;
  return offTableLine(props.availability, {keep, startsAt: c.startsAt, areaName: placeName.value, noun: noun.value, session: !!props.kept?.session, refusesPast: props.refusesPast, zone: zone.value});
});
const court = computed(() => (openPlay.value
    ? (choice.value?.place === 'elsewhere' ? 'Played somewhere else, so nothing is booked here.' : `Open play on ${dayLabel.value}: nothing to book${placeName.value ? `. ${placeName.value} is noted on the match` : ''}.`)
    : offTableText.value ?? courtLine({mode: props.mode, place: choice.value?.place, areaName: placeName.value, hold: hold.value, otherName: props.otherName, idle: idle.value, noun: noun.value, alreadyBooked: alreadyBooked.value})));
// X247 (TEST_R104 L-3): a ladder time that has passed can't be set (the dialog's Set is off): step 2 and the summary say
// so, never "keep the time with no court" or "No court booked".
const pastRefused = computed(() => {
  const c = choice.value ?? {};
  if (!props.refusesPast || !c.startsAt || !startHasPassed(c.startsAt)) return false;
  return !(props.kept?.scheduledAt && Math.abs(new Date(c.startsAt).getTime() - new Date(props.kept.scheduledAt).getTime()) < 60000);
});
// X247 (TEST_R104 M-1): a typed start in the table that keeps the court though its cell is Too short: the cell's own tip
// ("Too short for the match: Court 1 is free at 20:30, but it closes before 45 minutes is up. As an admin you can still
// book it.").
const rowKeptHint = computed(() => {
  const c = choice.value ?? {};
  if (!c.offTable || !time.value || c.place !== 'area') return null;
  const col = (grid.value?.columns ?? []).find((x) => x.area.id === c.areaId);
  const at = new Date(c.startsAt).getTime();
  return col?.cells.find((x) => new Date(x.startsAt).getTime() === at)?.tip ?? null;
});
// X244: step 2's hint for a time the table doesn't offer that keeps the court.
const offTableHint = computed(() => {
  const c = choice.value ?? {};
  const at = c.startsAt ? gridTime(c.startsAt, zone.value) : '';
  if (c.offTable === 'unchanged') return startHasPassed(c.startsAt) ? `The match started at ${at}: the table shows the starts still to come, and the match keeps its booking.` : `${at} is the match's own time: it keeps its booking.`;
  if (c.offTable === 'session') return `${at} isn't a start in the table: the whole session moves there with its bookings, if every ${noun.value} is free and open then.`;
  // F-FIX7: an admin's single match keeps its court there.
  if (c.offTable === 'match') return `${at} isn't a start in the table: the match keeps its ${noun.value}, if it is free then.`;
  return null;
});
// X244: the summary's court for a whole-session move with no court chosen: the session's courts, which keep their bookings.
const summaryPlace = computed(() => {
  if (pastRefused.value) return PAST_LADDER_PLACE; // X247 (TEST_R104 L-3): the time can't be set, so nothing changes
  return sessionKeepsCourts.value && !startHasPassed(choice.value?.startsAt) && props.availability?.sessionCourtNames?.length
      ? `Kept: ${listWords(props.availability.sessionCourtNames)}` : null;
});
// X214: Vue drops the space after a template's last word, so the two sentences are joined here ("…both sides. Court 2 is…").
const whatText = computed(() => whatHappensLine(props.note, court.value));
// X240 (TEST_R100 L-1): a payer's limit or rules are said once, by the payer picker (for the payer chosen); the API's
// warning about the default payer ("… Pick another payer, or choose the playing area later.") is not repeated under it.
const warnings = computed(() => (props.availability?.warnings ?? []).filter((w) => !(payerShown.value && isPayerWarning(w))));
const farAhead = computed(() => farAheadLine(props.availability, day.value, choice.value, {mode: props.mode, placeName: placeName.value, cell: cell.value}));

// ── Admin match length (B7c) ──
// X244 (TEST_R102 L-3): the choices are built on the competition's usual length (the API's usualMatchMinutes when the
// match has a one-off length, else its matchMinutes), never on a one-off, so a one-off is never "the usual length" nor
// listed twice; the match's own length is the one selected.
const baseMinutes = ref(null);
watch(() => props.availability, (a) => {
  if (a && baseMinutes.value == null) baseMinutes.value = a.usualMatchMinutes ?? a.matchMinutes;
}, {immediate: true});
const lengthChoices = computed(() => matchLengthChoices(baseMinutes.value ?? props.availability?.matchMinutes));
// X244 (TEST_R102 L-2): "Other" opens its minutes box even when its number is one of the presets.
const otherOpen = ref(false);
// The match's length now: the admin's choice (0 = back to the usual), else the match's own (a one-off counts).
const currentMinutes = computed(() => {
  const d = choice.value?.durationMinutes;
  if (d === 0) return baseMinutes.value;
  return d ?? props.availability?.matchMinutes ?? baseMinutes.value;
});
const lengthSelected = computed(() => {
  if (otherOpen.value) return 'other';
  const m = currentMinutes.value;
  return lengthChoices.value.some((c) => c.value === m) ? m : 'other';
});
const otherMinutes = ref(null);
watch(() => props.availability, (a) => {
  if (a && otherMinutes.value == null && lengthSelected.value === 'other') otherMinutes.value = currentMinutes.value;
}, {immediate: true});
// A one-off length on the match: picking the usual length sends 0 ("back to the usual", the API's MatchLength.FromRequest).
const hasOneOff = computed(() => props.availability?.usualMatchMinutes != null);
const pickLength = (c) => {
  if (c.value === 'other') {
    otherOpen.value = true;
    otherMinutes.value = otherMinutes.value ?? currentMinutes.value ?? 60;
    if (otherMinutes.value !== currentMinutes.value) {
      update({durationMinutes: otherMinutes.value});
      emit('reload', {minutes: otherMinutes.value});
    }
    return;
  }
  otherOpen.value = false;
  if (c.value === baseMinutes.value) {
    update({durationMinutes: hasOneOff.value ? 0 : null});
    emit('reload', {minutes: hasOneOff.value ? c.value : null});
    return;
  }
  update({durationMinutes: c.value});
  emit('reload', {minutes: c.value});
};
let otherTimer = null;
const onOtherMinutes = (v) => {
  const n = parseInt(v, 10);
  otherMinutes.value = Number.isFinite(n) ? n : null;
  clearTimeout(otherTimer);
  if (!Number.isFinite(n) || n < 5 || n > 1440) return;
  otherTimer = setTimeout(() => {
    update({durationMinutes: n});
    emit('reload', {minutes: n});
  }, 500);
};
const lengthLine = computed(() => `Just this match. The usual length is ${lengthWord(baseMinutes.value ?? props.availability?.matchMinutes)}.`);

// ── First selection, and every reload (another day, an admin's length) ──
const init = () => {
  const a = props.availability;
  if (!a?.dayList?.length) return;
  const c = choice.value ?? {};
  let date = c.date ?? (c.startsAt ? dateOfInstant(c.startsAt, zone.value) : null);
  let hhmm = c.time ?? (c.startsAt ? hhmmInZone(c.startsAt, zone.value) : null);
  if (!date) {
    const d = a.dayList.find((x) => !dayPassed(x)) ?? a.dayList[0];
    date = d.date;
    hhmm = hhmm ?? hhmmInZone((d.times ?? []).find((t) => !timeChipDisabled(t))?.startsAt, zone.value) ?? '19:00';
  }
  const state = dayLoadState(a, date, today.value, {asked: asked.value.has(date)});
  // X219 (R89 M-2): a new schedule's default time (tomorrow at the next half hour) is rarely a start in the table: it
  // moves once to the first start at or after it where the match fits on some court, so Book a court opens with a
  // selection. Nothing free: No court needed, saying why. Never a time someone chose (a reschedule, a proposal).
  if (c.snap && !c.touched && state === 'ready') {
    const s = firstFreeStart(a, date, startsAtFor(date, hhmm, zone.value));
    if (s) {
      date = s.date;
      hhmm = hhmmInZone(s.startsAt, zone.value);
      noFreeReason.value = '';
    } else if (!dayOpenPlay(findDay(a, date))) {
      noFreeReason.value = noFreeStartLine(dateWord(date), hhmm, noun.value);
      wantCourt.value = false;
    }
    update({snap: false});
  }
  // X219 (R89 M-3): the place is decided once the day is in (a day still to load would read as no court for ever).
  if (wantCourt.value === null && state !== 'load') {
    const t = timeAtClock(findDay(a, date), hhmm, zone.value);
    const preferred = props.preferredAreaId ?? (c.place === 'area' ? c.areaId : null);
    wantCourt.value = t ? gridDefaultPlace(props.mode, t, preferred, areaOrder.value).place === 'area' : c.place === 'area';
  }
  // The match's own day is not in the window (a reschedule further ahead than the first load): fetch from that day,
  // once; the place is picked when it lands (the existing booking shows as This match then).
  if (state === 'load') {
    update({date, time: hhmm, startsAt: startsAtFor(date, hhmm, zone.value)});
    ask(date);
    return;
  }
  applyWhen(date, hhmm);
};
watch(() => props.availability, init, {immediate: true});
</script>

<template>
  <div class="tpp" :class="{'tpp--loading': loading}">
    <div class="tpp-layout">
      <!-- 1 When -->
      <section class="tpp-step tpp-when" aria-labelledby="tpp-when-h">
        <h3 id="tpp-when-h" class="tpp-step-h"><span class="tpp-step-n" aria-hidden="true">1</span>When</h3>
        <simple-date-time-select v-model:date="whenDate" v-model:time="whenTime" stacked class="tpp-when-fields"/>
        <div v-if="!admin && lengthText" class="tpp-hint">{{ lengthText }}</div>
        <template v-if="admin">
          <div class="tl-label mb-2 mt-3">Match length</div>
          <div class="tl-seg">
            <button v-for="c in lengthChoices" :key="String(c.value)" type="button" class="tl-seg__opt"
                    :class="{'tl-seg__opt--active': lengthSelected === c.value}" @click="pickLength(c)">{{ c.label }}</button>
          </div>
          <v-text-field v-if="lengthSelected === 'other'" :model-value="otherMinutes" type="number" min="5" max="1440" step="5" suffix="minutes"
                        density="compact" hide-details class="tpp-other mt-2" @update:model-value="onOtherMinutes"/>
          <div class="tpp-hint">{{ lengthLine }}</div>
        </template>
      </section>

      <!-- 2 Book a court -->
      <section class="tpp-step tpp-court" aria-labelledby="tpp-court-h">
        <div class="tpp-court-head">
          <h3 id="tpp-court-h" class="tpp-step-h"><span class="tpp-step-n" aria-hidden="true">2</span>{{ bookStepTitle(noun) }}</h3>
          <div v-if="wantCourt !== false && selectedDate && !dayGone" class="tpp-daynav">
            <tl-icon-btn tooltip="Day before" icon="mdi-chevron-left" :disabled="!canEarlier" @click="stepDay(-1)"/>
            <span class="tpp-daynav-t">{{ dayLabel }}</span>
            <tl-icon-btn tooltip="Day after" icon="mdi-chevron-right" @click="stepDay(1)"/>
          </div>
        </div>

        <div class="tl-seg tpp-seg" role="group" :aria-label="bookStepTitle(noun)">
          <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': wantCourt !== false}" :aria-pressed="wantCourt !== false" @click="pickCourt(true)">
            {{ bookStepTitle(noun) }}
          </button>
          <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': wantCourt === false}" :aria-pressed="wantCourt === false" @click="pickCourt(false)">
            No {{ noun }} needed
          </button>
        </div>

        <!-- X219: wantCourt is null only while the chosen day loads (decided when it lands): the skeleton shows -->
        <template v-if="wantCourt !== false">
          <div v-if="dayGone" class="tpp-empty">
            <v-icon size="24" color="charcoal" icon="mdi-calendar-remove-outline"/>
            <div class="tpp-empty-t">That day has passed</div>
            <div class="tpp-empty-s">Pick a day from today in When.</div>
          </div>
          <div v-else-if="dayState === 'failed'" class="tpp-empty" role="alert">
            <v-icon size="24" color="charcoal" icon="mdi-cloud-alert-outline"/>
            <div class="tpp-empty-t">{{ pluralCap }} for {{ dayLabel }} didn't load</div>
            <div class="tpp-empty-s">Try again, or pick No {{ noun }} needed.</div>
            <v-btn variant="outlined" rounded="pill" size="small" class="mt-3" prepend-icon="mdi-refresh" @click="retryDay">Retry</v-btn>
          </div>
          <!-- Grant 2026-11-02: the same table-shaped skeleton as /book (BookingDayGrid with no grid), so the day lands in place -->
          <div v-else-if="!day" class="tpp-grid-card" aria-busy="true">
            <booking-day-grid :grid="null" loading no-drag/>
          </div>
          <template v-else-if="openPlay">
            <div class="tpp-empty">
              <v-icon size="24" color="charcoal" icon="mdi-weather-sunny"/>
              <div class="tpp-empty-t">Open play on {{ dayLabel }}</div>
              <div class="tpp-empty-s">{{ day.note || 'Nothing to book: just turn up.' }} You can note a {{ noun }} on the match.</div>
            </div>
            <div class="tpp-places">
              <button v-for="p in openPlaces" :key="p.key" type="button" class="tpp-place" :class="{'tpp-place--on': openPlaceKey === p.key}"
                      :aria-pressed="openPlaceKey === p.key" @click="pickOpenPlace(p)">{{ p.place === 'later' ? 'No ' + noun : p.label }}</button>
            </div>
          </template>
          <template v-else>
            <div class="tpp-grid-card">
              <booking-day-grid :grid="grid" :selected="selected" :loading="loading" :currency="availability.currency || 'gbp'" :noun-plural="words.plural" no-drag
                                @tap="onTap" @explain="onExplain">
                <template #corner><booking-legend :items="legendItems"/></template>
              </booking-day-grid>
            </div>
            <div class="tpp-hint" aria-live="polite">
              <template v-if="explain">{{ explain }}</template>
              <template v-else-if="pastRefused">{{ PAST_LADDER_HINT }}</template>
              <template v-else-if="rowKeptHint">{{ rowKeptHint }}</template>
              <template v-else-if="nothingLeft">Nothing is free for a whole match on {{ dayLabel }}. Try another day, or pick No {{ noun }} needed.</template>
              <template v-else-if="offSlot">{{ offTableHint || offSlotLine(choice.time, noun) }}</template>
              <template v-else-if="day.note">{{ day.note }}</template>
              <template v-else>Pick a start. The booking covers the {{ bookingNoun(availability) }}, {{ lengthWord(bookingMinutesOf(availability, choice)) }}, from there.</template>
            </div>
          </template>
        </template>
        <template v-else>
          <div v-if="noFreeReason" class="tpp-hint tpp-hint--lead" role="status">{{ noFreeReason }}</div>
          <choice-radio-list v-model="noCourtChoice" :items="noCourtItems" :label="`No ${noun} needed`"/>
        </template>
      </section>

      <!-- 3 Summary -->
      <section class="tpp-step tpp-sum" aria-labelledby="tpp-sum-h">
        <h3 id="tpp-sum-h" class="tpp-step-h"><span class="tpp-step-n" aria-hidden="true">3</span>Check and confirm</h3>
        <div class="tpp-sum-card">
          <div class="tpp-sum-when">
            <v-icon size="18" icon="mdi-calendar-clock-outline"/>
            <span>{{ summary.date || 'Pick a day' }}<template v-if="summary.time"> · {{ summary.time }}</template></span>
          </div>
          <div v-if="summary.session" class="tpp-sum-booked">{{ summary.session }}</div>
          <div v-if="summary.booked" class="tpp-sum-booked">{{ summary.booked }}</div>
          <div class="tpp-sum-row">
            <span class="tpp-sum-k">{{ nounCap }}</span>
            <span class="tpp-sum-v" :class="{'tpp-sum-v--none': (choice.place !== 'area' && !summaryPlace) || pastRefused}">{{ summaryPlace || summary.place || `No ${noun} booked` }}</span>
          </div>
          <div v-if="summary.price" class="tpp-sum-row">
            <span class="tpp-sum-k">Price</span>
            <span class="tpp-sum-v">{{ summary.price }}</span>
          </div>
        </div>

        <!-- Hold (B7b) -->
        <v-checkbox v-if="holdShown" v-model="hold" color="primary" hide-details density="compact" class="tpp-hold mt-2">
          <template #label>
            <div class="ml-2">
              <div>{{ holdLabel(placeName, otherName) }}</div>
              <small class="text-textSubtle">{{ holdHelp(otherName) }}</small>
            </div>
          </template>
        </v-checkbox>

        <!-- Who pays (BE9) -->
        <match-payer-picker v-if="payerShown" v-model="payer" :options="payers" :refusal="payerRefused" :hint="payerHint" class="mt-3"/>
        <div v-else-if="split" class="tpp-hint mt-2">{{ splitText }}</div>
        <div v-else-if="priceLine" class="tpp-hint mt-2">{{ priceLine }}</div>

        <div v-if="pastClosingText" class="tpp-hint mt-2">{{ pastClosingText }}</div>
        <v-alert v-for="w in warnings" :key="w" type="info" variant="tonal" density="compact" class="mt-3">{{ w }}</v-alert>
        <v-alert v-if="farAhead" type="info" variant="tonal" density="compact" class="mt-3">
          <div class="font-weight-bold">We'll book {{ placeName || `an available ${noun}` }} on {{ dateWord(day.date) }}</div>
          <div>{{ farAhead }}</div>
        </v-alert>
        <div class="tpp-what">
          <v-icon size="16" icon="mdi-information-outline"/>
          <span>{{ whatText }}</span>
        </div>
      </section>
    </div>

    <!-- X237: an admin's Book anyway on a start that runs past closing -->
    <tl-dialog v-model="closingOpen" :title="closingAsk?.words.title" :max-width="400">
      <p class="tpp-closing-p">{{ closingAsk?.words.body }}</p>
      <template #actions>
        <div class="tpp-closing-actions">
          <v-btn variant="text" @click="closingOpen = false">{{ closingAsk?.words.no }}</v-btn>
          <v-btn color="primary" variant="flat" @click="bookAnyway">{{ closingAsk?.words.yes }}</v-btn>
        </div>
      </template>
    </tl-dialog>
  </div>
</template>

<style scoped lang="scss">
/* X237: the Book anyway confirm's sentence */
.tpp-closing-p {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: rgb(var(--v-theme-charcoal));
}
/* X214: the picker lays itself out by its own width (a desktop dialog, a 600px ladder dialog, a phone). */
.tpp {
  container: tpp / inline-size;
}

.tpp--loading .tpp-grid-card {
  pointer-events: none;
}

.tpp-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: "when" "court" "sum";
  gap: 22px;
}

.tpp-when { grid-area: when; }
.tpp-court { grid-area: court; min-width: 0; }
.tpp-sum { grid-area: sum; }

/* Wide: When and the summary on the left (the summary sticky in the dialog's scroll), the table on the right. */
@container tpp (min-width: 860px) {
  .tpp-layout {
    grid-template-columns: 300px minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas: "when court" "sum court";
    column-gap: 28px;
    row-gap: 22px;
  }

  .tpp-sum {
    align-self: start;
    position: sticky;
    top: 0;
  }
}

.tpp-step-h {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: rgb(var(--v-theme-text));
}

.tpp-step-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  border-radius: 999px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-size: 12px;
  font-weight: 800;
}

/* The date and time fields sit flush with the step (v-row's gutters off). */
.tpp-when-fields {
  margin: 0;

  > :deep(.v-col) {
    padding: 0 0 12px;
  }
}

.tpp-court-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 36px;
  margin-bottom: 12px;

  .tpp-step-h {
    margin: 0;
  }
}

.tpp-daynav {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tpp-daynav-t {
  min-width: 96px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}

/* Narrow: the two options share the width and may wrap ("No playing area needed" at 360px); wide: they hug. */
.tpp-seg {
  width: 100%;
  margin-bottom: 12px;

  > .tl-seg__opt {
    flex: 1 1 0;
    min-width: 0;
    white-space: normal;
    text-align: center;
  }
}

@container tpp (min-width: 560px) {
  .tpp-seg {
    width: fit-content;

    > .tl-seg__opt {
      flex: 0 0 auto;
      white-space: nowrap;
    }
  }
}

/* X214 (Grant 2026-10-28: rows showed above the stuck header): the card is the table's OWN up-and-down scroller, with
   no padding, border gap or anything else above the header inside it, so the header sticks flush at its very top
   (top: 0) and nothing scrolls into view above it (the dialog body's top padding is outside this scroller). Sideways
   the grid's own .bw-scroll scrolls, with the time column pinned left. Both are opaque and over every cell. */
.tpp-grid-card {
  --v-layout-top: 0px;
  container-type: inline-size;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: max(320px, calc(100dvh - 300px));
  overscroll-behavior: contain;
  background: rgb(var(--v-theme-surface));

  :deep(.bw-head) {
    top: 0;
    z-index: 5;
    background: rgb(var(--v-theme-surface));
  }

  :deep(.bw-corner) {
    background: rgb(var(--v-theme-surface));
  }

  :deep(.btc) {
    z-index: 4;
    background: rgb(var(--v-theme-surface));
  }

  /* The columns share the card's width (a comfortable minimum; sideways scroll beyond it). */
  :deep(.bw) {
    --bw-col: max(132px, calc((100cqw - var(--bw-tw) - 2px) / max(1, var(--bw-cols))));
  }
}

.tpp-hint {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.5;
  margin: 8px 2px 0;
  min-height: 18px;
}

.tpp-hint--lead {
  margin: 0 2px 8px;
}

.tpp-empty {
  text-align: center;
  padding: 24px 16px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
}

.tpp-empty-t {
  font-size: 14px;
  font-weight: 700;
  margin-top: 6px;
}

.tpp-empty-s {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}

.tpp-places {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.tpp-place {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }
}

.tpp-place--on {
  border-color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

/* The summary: the when on one bold line, the court and the price as two quiet rows. */
.tpp-sum-card {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  padding: 12px 14px;
  background: rgb(var(--v-theme-surface));
}

.tpp-sum-when {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));

  .v-icon {
    color: rgb(var(--v-theme-primary));
  }
}

.tpp-sum-booked {
  margin: 4px 0 0 26px;
  font-size: 12px;
  line-height: 1.4;
  color: rgb(var(--v-theme-textSubtle));
}

.tpp-sum-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  font-size: 13px;
}

.tpp-sum-k {
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 600;
}

.tpp-sum-v {
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  text-align: right;

  &--none {
    font-weight: 600;
    color: rgb(var(--v-theme-charcoal));
  }
}

.tpp-other {
  max-width: 180px;
}

.tpp-hold :deep(.v-label) {
  opacity: 1;
}

.tpp-what {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));

  .v-icon {
    margin-top: 2px;
    flex-shrink: 0;
  }
}

/* F-FIX6 (TEST_R99 L-3): the past-closing confirm's two buttons on one row; on a narrow phone they stack full width,
   Book anyway on top, instead of wrapping into two right-aligned rows. */
.tpp-closing-actions { display: flex; justify-content: flex-end; gap: 8px; width: 100%; }
@media (max-width: 400px) {
  .tpp-closing-actions { flex-direction: column-reverse; align-items: stretch; }
  .tpp-closing-actions > .v-btn { width: 100%; margin: 0 !important; }
}
</style>
